import type Combatant from "@/encounter/model/Combatant.ts";
import type {Action} from "@/characters/model/Character.ts";
import {useCharacterDetailsViewModel} from "@/characters/admin/details/useCharacterDetailsViewModel.ts";
import CharacterStats from "@/characters/admin/details/CharacterStats.tsx";
import MarkdownContent from "@/components/markdown/MarkdownContent.tsx";
import SpellcastingView from "@/characters/admin/details/SpellcastingView.tsx";
import type {CombatantDetail} from "./CombatantDockLayout.tsx";
import {Button} from "@/components/ui/button.tsx";
import {ChevronDown, ChevronsDownUp, ChevronsUpDown} from "lucide-react";
import {useId} from "react";

type ExpansionProps = {
    expandedActions: Record<string, boolean>;
    onExpansionChange: (entries: Record<string, boolean>) => void;
};

export default function CombatantDetailContent({combatant, detail, expandedActions, onExpansionChange}: {
    combatant: Combatant; detail: CombatantDetail;
} & ExpansionProps) {
    const character = combatant.character;
    const {actions, traits, reactions, legendaryActions, detailRows} = useCharacterDetailsViewModel(character);
    if (detail === "character") return <div className="space-y-2">
        <h3 className="pl-4 pr-12 font-semibold">Charakterdetails</h3>
        <p className="px-4 text-xs text-muted-foreground">
            {[character.alignment, character.race, character.classes?.join(", ")].filter(Boolean).join(" · ")}
        </p>
        <p className="px-4 text-xs text-muted-foreground">Charakterwerte der Vorlage</p>
        <CharacterStats character={character} detailRows={detailRows}/>
    </div>;
    if (detail === "spells") return character.spellcasting ? <div className="[&_header]:pr-12"><SpellcastingView spellcasting={character.spellcasting}/></div>
        : <p className="px-4 text-muted-foreground">Keine Zauber vorhanden.</p>;
    const expansion = {combatantId: combatant.id, expandedActions, onExpansionChange};
    if (detail === "traits") return <ActionGroup title="Merkmale" actions={traits} {...expansion}/>;
    return <div className="space-y-4"><ActionGroup title="Aktionen" actions={actions} {...expansion}/>
        {reactions.length > 0 && <ActionGroup title="Reaktionen" actions={reactions} {...expansion}/>}
        {legendaryActions.length > 0 && <ActionGroup title="Legendäre Aktionen" actions={legendaryActions} {...expansion}/>}
    </div>;
}

function ActionGroup({title, actions, combatantId, expandedActions, onExpansionChange}: {
    title: string; actions: Action[]; combatantId: string;
} & ExpansionProps) {
    const entries = actions.map((action, index) => {
        // Names are stable when different actions are reordered; duplicate names get an occurrence number.
        const occurrence = actions.slice(0, index).filter(previous => previous.name === action.name).length;
        const key = JSON.stringify([combatantId, action.type, action.name, occurrence]);
        const expanded = expandedActions[key] ?? startsExpanded(action.value);
        return {action, key, expanded};
    });
    const expandAll = (expanded: boolean) => onExpansionChange(Object.fromEntries(entries.map(entry => [entry.key, expanded])));
    return <section className="space-y-2 px-3" aria-label={title}>
        <div className="flex items-center gap-1 pr-10">
            <h4 className="min-w-0 flex-1 font-semibold">{title} · {actions.length}</h4>
            {actions.length > 0 && <>
                <Button type="button" variant="ghost" size="icon-sm"
                    aria-label={`Alle ${title} aufklappen`} title="Alle aufklappen" disabled={entries.every(entry => entry.expanded)}
                    onClick={() => expandAll(true)}><ChevronsUpDown aria-hidden="true"/></Button>
                <Button type="button" variant="ghost" size="icon-sm"
                    aria-label={`Alle ${title} einklappen`} title="Alle einklappen" disabled={entries.every(entry => !entry.expanded)}
                    onClick={() => expandAll(false)}><ChevronsDownUp aria-hidden="true"/></Button>
            </>}
        </div>
        {actions.length === 0 && <p className="text-xs text-muted-foreground">Keine Einträge vorhanden.</p>}
        {entries.map(({action, key, expanded}) => <ActionEntry key={key} action={action} expanded={expanded}
            onToggle={() => onExpansionChange({[key]: !expanded})}/>)}
    </section>;
}

function startsExpanded(markdown: string): boolean {
    const text = markdown.trim();
    return text.length <= 200 && !/\n\s*\n|^\s*(?:[-+*]|\d+[.)])\s|^\s*(?:#{1,6}\s|```|~~~|>\s|\|)/m.test(text);
}

function actionPreview(markdown: string): string {
    let text = markdown.replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1");
    // Strip all directive wrappers, including nested directives, but retain their readable labels.
    let previous: string;
    do {
        previous = text;
        text = text.replace(/:{1,3}[\w-]+\[([^\[\]]*)\](?:\{[^}]*\})?/g, "$1");
    } while (text !== previous);
    text = text.replace(/^\s*:{2,}[\w-]*(?:\{[^}]*\})?\s*$/gm, "")
        .replace(/:[a-z][\w-]*\{[^}]*\}/gi, "")
        .replace(/[*_`#~]/g, "").replace(/\s+/g, " ").trim();
    return text.length <= 40 ? text : `${text.slice(0, 37).replace(/\s+\S*$/, "")} …`;
}

function ActionEntry({action, expanded, onToggle}: {action: Action; expanded: boolean; onToggle: () => void}) {
    const contentId = useId();
    return <div className="rounded-lg border p-2 text-[0.8125rem] leading-snug">
        <button type="button" className={expanded ? "inline-flex items-baseline gap-1 text-left" : "flex w-full items-center gap-1 text-left"}
            aria-label={action.name} aria-expanded={expanded} aria-controls={contentId} onClick={onToggle}>
            <ChevronDown className={`size-3 shrink-0 self-center ${expanded ? "" : "-rotate-90"}`} aria-hidden="true"/>
            <span className={expanded ? "font-semibold" : "min-w-0 truncate"}>
                <span className="font-semibold">{action.name}</span>.
                {!expanded && <span className="ml-1 text-muted-foreground">{actionPreview(action.value)}</span>}
            </span>
        </button>
        <div id={contentId} hidden={!expanded} className="inline">
            {expanded && <MarkdownContent markdown={action.value} className="inline leading-snug [&_p]:mb-1.5 [&_ul]:my-1.5 [&>p:first-child]:ml-1 [&>p:first-child]:inline"/>}
        </div>
    </div>;
}
